import Modal from '@/app/_components/modal';
import { Sheet } from '@/app/_components/Sheet';
import { ClinicHeader } from '@/app/_components/widgets/listing/cardV3/ClinicHeader';
import { PriceSection } from '@/app/_components/widgets/listing/cardV3/PriceSection';
import { useIsDesktop } from '@/app/_hooks/useIsScreen';
import { SpecialityByIdDocument } from '@/types-aggregatore';
import { useQuery } from '@apollo/client';
import { ELButton } from '@davincihealthcare/elty-design-system-react';
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

type SpecialityProps = {
  specialityId: string | null;
  specialityName: string;
  price: number | undefined;
  hasDynamicPrice: boolean;
  hasDescription: boolean;
};

type WorkgroupProps = {
  logoUrl: string | undefined;
  clinicName: string;
  clinicLink: string;
  completeAddress: string;
  isLicensed: boolean;
  isDomicile: boolean;
};

/**
 * Wrapper component that conditionally renders either a button or a fragment based on onClick prop
 */
const SpecialityWrapper = ({ onClick, children }: { onClick: (() => void) | undefined; children: React.ReactNode }) => {
  if (!onClick) {
    return <>{children}</>;
  }

  return (
    <button className="flex flex-col gap-1 overflow-hidden text-left" onClick={onClick}>
      {children}
    </button>
  );
};

export const Speciality = ({
  specialityName,
  specialityId,
  logoUrl,
  clinicName,
  clinicLink,
  completeAddress,
  isLicensed,
  isDomicile,
  price,
  hasDynamicPrice,
  hasDescription,
  onChooseDate,
}: SpecialityProps & WorkgroupProps & { onChooseDate: () => void }) => {
  const [isSpecialityDescriptionModalOpen, setIsSpecialityDescriptionModalOpen] = useState(false);

  return (
    <>
      {isSpecialityDescriptionModalOpen && specialityId && (
        <SpecializationDescriptionModal
          onClose={() => setIsSpecialityDescriptionModalOpen(false)}
          specialityId={specialityId}
          logoUrl={logoUrl}
          clinicName={clinicName}
          clinicLink={clinicLink}
          completeAddress={completeAddress}
          isLicensed={isLicensed}
          isDomicile={isDomicile}
          price={price}
          hasDynamicPrice={hasDynamicPrice}
          onChooseDate={onChooseDate}
        />
      )}

      <SpecialityWrapper onClick={hasDescription && specialityId ? () => setIsSpecialityDescriptionModalOpen(true) : undefined}>
        <h3 className={`break-words text-base font-semibold text-neutral-darker ${hasDescription ? 'cursor-pointer underline' : ''}`}>
          {specialityName}
        </h3>
      </SpecialityWrapper>
    </>
  );
};

const SpecializationDescriptionModal = ({
  specialityId,
  logoUrl,
  clinicName,
  clinicLink,
  completeAddress,
  isLicensed,
  onClose,
  price,
  onChooseDate,
  hasDynamicPrice,
  isDomicile,
}: WorkgroupProps & { onClose: () => void; onChooseDate: () => void; specialityId: string } & Pick<
    SpecialityProps,
    'price' | 'hasDynamicPrice'
  >) => {
  const { data } = useQuery(SpecialityByIdDocument, {
    variables: {
      id: specialityId,
    },
  });

  const onChooseDateAndClose = () => {
    onChooseDate();
    onClose();
  };

  const description = data?.specialityById?.eltyDescription;
  const medicalInstructions = data?.specialityById?.medicalInstructions;
  const specialityName = data?.specialityById?.label;
  const isDesktopAndSmaller = useIsDesktop();

  if (!description) return null;

  const descriptionContent = (
    <>
      <ClinicHeader
        distance={null}
        logoSrc={logoUrl}
        clinicLink={clinicLink}
        isDomicile={isDomicile}
        clinicName={clinicName}
        isLicensed={isLicensed}
        completeAddress={completeAddress}
      />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className={`${isDesktopAndSmaller ? 'text-base' : 'text-lg'} font-semibold text-primary-active`}>{specialityName}</h3>
          <PriceSection hasDynamicPrice={hasDynamicPrice} isLicensedClinic={isLicensed} price={price} hasAvailableSlots />
        </div>
        <p className="text-sm text-neutral-darker">{description}</p>
      </div>
      {medicalInstructions && (
        <div className="flex flex-col rounded-lg bg-neutral-surface-raised p-4">
          <div className="text-md flex items-center gap-2 font-medium text-brandBlue-500 md:text-xl">
            <ExclamationCircleIcon className="size-5 flex-shrink-0 text-brandBlue-500" />
            <div>Importante</div>
          </div>
          <div className="mt-4 text-xs font-normal text-neutral-darker md:text-sm">{medicalInstructions}</div>
        </div>
      )}
    </>
  );

  const actions = (
    <>
      {isLicensed && (
        <ELButton size="large" fullWidth variant="filled" color="primary" label="Scegli orario" onClick={onChooseDateAndClose} />
      )}
      <ELButton
        size="large"
        fullWidth
        variant="text"
        color="primary"
        label="Chiudi"
        onClick={() => {
          onClose();
        }}
      />
    </>
  );

  return (
    <>
      {!isDesktopAndSmaller && (
        <Sheet isOpen={true} onClose={onClose}>
          <div className="flex flex-col gap-8 px-20 py-16">
            <h3 className="font-semibold text-primary md:text-2xl">Descrizione</h3>
            {descriptionContent}
            <div className="flex gap-4">{actions} </div>
          </div>
        </Sheet>
      )}
      {isDesktopAndSmaller && (
        <Modal
          className="absolute bottom-0 mb-0 ml-auto flex h-[90%] w-[100vw] max-w-[100vw] flex-col items-center justify-between gap-4 rounded-t-xl px-3 py-5"
          onClose={onClose}
          isModalOpen
        >
          <div className="flex w-full flex-col justify-start">
            <div className="flex w-full items-center justify-between pb-6 pt-2">
              <div />
              <h1 className="text-base font-semibold text-primary">Descrizione</h1>
              <XMarkIcon className="size-6 cursor-pointer" onClick={onClose} />
            </div>
            <div className="flex max-h-full w-full flex-col items-stretch justify-start gap-6 overflow-y-auto">{descriptionContent}</div>
          </div>
          <div className="flex w-full flex-col items-center justify-end gap-5 py-5">{actions}</div>
        </Modal>
      )}
    </>
  );
};
